'use client';

import { useReporting } from '@/hooks/useReporting';
import useRouteInfo from '@/hooks/useRouteInfo';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { useCallback } from 'react';

export function usePinkLeopardTracking() {
  const { trackEvent } = useReporting();
  const { getFromRouterInfo } = useRouteInfo();

  /**
   * 神策埋点：页面浏览埋点
   * @TrackType sensorsdata
   * @EventType PinkLeopardGlassesPage_View
   * @EventDesc 粉豹纹专题页页面浏览
   * @EventParams {
   *   page_belong_ab: AB版本标识,
   *   refer_page_name: 前向页面名称,
   *   refer_url_path: 前向页面路径,
   * }
   */
  const trackPageView = useCallback(() => {
    try {
      const { refer_page_name, refer_url_path } = getFromRouterInfo();
      trackEvent(
        {
          eventName: 'PinkLeopardGlassesPage_View',
          properties: {
            page_belong_ab: 'B版',
            refer_page_name,
            refer_url_path,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('PinkLeopardGlassesPage_View tracking error:', error);
    }
  }, [trackEvent, getFromRouterInfo]);

  /**
   * 神策埋点：Banner曝光埋点
   * @TrackType sensorsdata
   * @EventType PinkLeopardGlassesPageBanner_Exp
   * @EventDesc 粉豹纹专题页Banner曝光
   * @EventParams {
   *   mkt_name: 版位名称,
   *   page_belong_ab: AB版本标识,
   * }
   */
  const trackBannerExposure = useCallback(
    (mktName = 'banner1') => {
      try {
        trackEvent(
          {
            eventName: 'PinkLeopardGlassesPageBanner_Exp',
            properties: {
              mkt_name: mktName,
              page_belong_ab: 'B版',
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('PinkLeopardGlassesPageBanner_Exp tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 神策埋点：Banner点击埋点
   * @TrackType sensorsdata
   * @EventType PinkLeopardGlassesPageBanner_Click
   * @EventDesc 粉豹纹专题页Banner点击
   * @EventParams {
   *   mkt_name: 版位名称,
   *   page_belong_ab: AB版本标识,
   * }
   */
  const trackBannerClick = useCallback(
    (mktName = 'banner1') => {
      try {
        trackEvent(
          {
            eventName: 'PinkLeopardGlassesPageBanner_Click',
            properties: {
              mkt_name: mktName,
              page_belong_ab: 'B版',
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('PinkLeopardGlassesPageBanner_Click tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 神策埋点：商品坑位点击埋点
   * @TrackType sensorsdata
   * @EventType PinkLeopardGlassesPageProductPits_Click
   * @EventDesc 粉豹纹专题页商品坑位点击
   * @EventParams {
   *   mkt_name: 版位名称,
   *   pits_index: 坑位索引,
   *   shop_sku: 销售商品sku,
   *   business_sku: 平台sku,
   *   product_sku: 商品sku,
   *   page_belong_ab: AB版本标识,
   * }
   */
  const trackProductPitsClick = useCallback(
    (product: any, index: number, mktName: string) => {
      try {
        trackEvent(
          {
            eventName: 'PinkLeopardGlassesPageProductPits_Click',
            properties: {
              mkt_name: mktName,
              pits_index: index,
              shop_sku: product.shop_sku,
              business_sku: product.business_sku,
              product_sku: product.sku,
              page_belong_ab: 'B版',
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('PinkLeopardGlassesPageProductPits_Click tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 神策埋点：商品坑位曝光埋点
   * @TrackType sensorsdata
   * @EventType PinkLeopardGlassesPageProductPits_Exp
   * @EventDesc 粉豹纹专题页商品坑位曝光
   * @EventParams {
   *   mkt_name: 版位名称,
   *   pits_index: 坑位索引,
   *   shop_sku: 销售商品sku,
   *   business_sku: 平台sku,
   *   product_sku: 商品sku,
   *   page_belong_ab: AB版本标识,
   * }
   */
  const trackProductPitsExposure = useCallback(
    (product: any, index: number, mktName: string) => {
      try {
        trackEvent(
          {
            eventName: 'PinkLeopardGlassesPageProductPits_Exp',
            properties: {
              mkt_name: mktName,
              pits_index: index,
              shop_sku: product.shop_sku,
              business_sku: product.business_sku,
              product_sku: product.sku,
              page_belong_ab: 'B版',
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('PinkLeopardGlassesPageProductPits_Exp tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 神策埋点：购买按钮点击埋点
   * @TrackType sensorsdata
   * @EventType PinkLeopardGlassesPageProductBuyNowBtn_Click
   * @EventDesc 粉豹纹专题页购买按钮点击
   * @EventParams {
   *   mkt_name: 版位名称,
   *   pits_index: 坑位索引,
   *   shop_sku: 销售商品sku,
   *   business_sku: 平台sku,
   *   product_sku: 商品sku,
   *   page_belong_ab: AB版本标识,
   * }
   */
  const trackBuyNowClick = useCallback(
    (product: any, index: number, mktName: string) => {
      try {
        trackEvent(
          {
            eventName: 'PinkLeopardGlassesPageProductBuyNowBtn_Click',
            properties: {
              mkt_name: mktName,
              pits_index: index,
              shop_sku: product.shop_sku,
              business_sku: product.business_sku,
              product_sku: product.sku,
              page_belong_ab: 'B版',
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('PinkLeopardGlassesPageProductBuyNowBtn_Click tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 神策埋点：商品颜色按钮点击埋点
   * @TrackType sensorsdata
   * @EventType PinkLeopardGlassesPageProductPitsColorBtn_Click
   * @EventDesc 粉豹纹专题页商品颜色按钮点击
   * @EventParams {
   *   mkt_name: 版位名称,
   *   color_name: 颜色名称,
   *   page_belong_ab: AB版本标识,
   *   pits_index: 坑位索引,
   * }
   */
  const trackColorClick = useCallback(
    (color: any, pitsIndex: number, mktName: string) => {
      try {
        trackEvent(
          {
            eventName: 'PinkLeopardGlassesPageProductPitsColorBtn_Click',
            properties: {
              mkt_name: mktName,
              color_name: color.color,
              page_belong_ab: 'B版',
              pits_index: pitsIndex,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('PinkLeopardGlassesPageProductPitsColorBtn_Click tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 神策埋点：商品列表坑位曝光埋点
   * @TrackType sensorsdata
   * @EventType PinkLeopardGlassesPageProductListPits_Exp
   * @EventDesc 粉豹纹专题页商品列表坑位曝光
   * @EventParams {
   *   mkt_name: 版位名称,
   *   pits_index: 坑位索引,
   *   shop_sku: 销售商品sku,
   *   business_sku: 平台sku,
   *   product_sku: 商品sku,
   *   page_belong_ab: AB版本标识,
   * }
   */
  const trackProductListPitsExposure = useCallback(
    (product: any, index: number, mktName: string) => {
      try {
        trackEvent(
          {
            eventName: 'PinkLeopardGlassesPageProductListPits_Exp',
            properties: {
              mkt_name: mktName,
              pits_index: index,
              shop_sku: product.shop_sku,
              business_sku: product.business_sku,
              product_sku: product.sku,
              page_belong_ab: 'B版',
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('PinkLeopardGlassesPageProductListPits_Exp tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 神策埋点：商品列表坑位点击埋点
   * @TrackType sensorsdata
   * @EventType PinkLeopardGlassesPageProductListPits_Click
   * @EventDesc 粉豹纹专题页商品列表坑位点击
   * @EventParams {
   *   mkt_name: 版位名称,
   *   pits_index: 坑位索引,
   *   shop_sku: 销售商品sku,
   *   business_sku: 平台sku,
   *   product_sku: 商品sku,
   *   page_belong_ab: AB版本标识,
   * }
   */
  const trackProductListPitsClick = useCallback(
    (product: any, index: number, mktName: string) => {
      try {
        trackEvent(
          {
            eventName: 'PinkLeopardGlassesPageProductListPits_Click',
            properties: {
              mkt_name: mktName,
              pits_index: index,
              shop_sku: product.shop_sku,
              business_sku: product.business_sku,
              product_sku: product.sku,
              page_belong_ab: 'B版',
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('PinkLeopardGlassesPageProductListPits_Click tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 神策埋点：照片墙曝光埋点
   * @TrackType sensorsdata
   * @EventType PinkLeopardGlassesPagePhotoPits_Exp
   * @EventDesc 粉豹纹专题页照片墙曝光
   * @EventParams {
   *   mkt_name: 版位名称,
   *   pits_index: 坑位索引,
   *   page_belong_ab: AB版本标识,
   * }
   */
  const trackPhotoWallExposure = useCallback(
    (mktName: string, pitsIndex: number) => {
      try {
        trackEvent(
          {
            eventName: 'PinkLeopardGlassesPagePhotoPits_Exp',
            properties: {
              mkt_name: mktName,
              pits_index: pitsIndex,
              page_belong_ab: 'B版',
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('PinkLeopardGlassesPagePhotoPits_Exp tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 神策埋点：照片墙点击埋点
   * @TrackType sensorsdata
   * @EventType PinkLeopardGlassesPagePhotoPits_Click
   * @EventDesc 粉豹纹专题页照片墙点击
   * @EventParams {
   *   mkt_name: 版位名称,
   *   pits_index: 坑位索引,
   *   page_belong_ab: AB版本标识,
   * }
   */
  const trackPhotoWallClick = useCallback(
    (mktName: string, pitsIndex: number) => {
      try {
        trackEvent(
          {
            eventName: 'PinkLeopardGlassesPagePhotoPits_Click',
            properties: {
              mkt_name: mktName,
              pits_index: pitsIndex,
              page_belong_ab: 'B版',
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('PinkLeopardGlassesPagePhotoPits_Click tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 神策埋点：VIEW MORE按钮曝光埋点
   * @TrackType sensorsdata
   * @EventType PinkLeopardGlassesPageMktViewMoreBtn_Exp
   * @EventDesc 粉豹纹专题页VIEW MORE按钮曝光
   * @EventParams {
   *   mkt_name: 版位名称,
   *   page_belong_ab: AB版本标识,
   * }
   */
  const trackViewMoreExposure = useCallback(
    (mktName = '商品列表位') => {
      try {
        trackEvent(
          {
            eventName: 'PinkLeopardGlassesPageMktViewMoreBtn_Exp',
            properties: {
              mkt_name: mktName,
              page_belong_ab: 'B版',
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('PinkLeopardGlassesPageMktViewMoreBtn_Exp tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 神策埋点：VIEW MORE按钮点击埋点
   * @TrackType sensorsdata
   * @EventType PinkLeopardGlassesPageMktViewMoreBtn_Click
   * @EventDesc 粉豹纹专题页VIEW MORE按钮点击
   * @EventParams {
   *   mkt_name: 版位名称,
   *   page_belong_ab: AB版本标识,
   * }
   */
  const trackViewMoreClick = useCallback(
    (mktName = '商品列表位') => {
      try {
        trackEvent(
          {
            eventName: 'PinkLeopardGlassesPageMktViewMoreBtn_Click',
            properties: {
              mkt_name: mktName,
              page_belong_ab: 'B版',
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('PinkLeopardGlassesPageMktViewMoreBtn_Click tracking error:', error);
      }
    },
    [trackEvent],
  );

  return {
    trackPageView,
    trackBannerExposure,
    trackBannerClick,
    trackProductPitsClick,
    trackProductPitsExposure,
    trackBuyNowClick,
    trackColorClick,
    trackProductListPitsExposure,
    trackProductListPitsClick,
    trackPhotoWallExposure,
    trackPhotoWallClick,
    trackViewMoreExposure,
    trackViewMoreClick,
  };
}
